import { useCallback, useRef } from "react";
import { isSameDay } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { SubscriptionData } from "../api/useWebSockets";
import { TECHNICIAN_WEEK_CLINICS_QUERY_KEY_VALUE } from "../api/useTechnicianCurrentWeekClinics";

const INVALIDATE_DEBOUNCE_MS = 500;

// confirmed fires for every future booking edit, so it only invalidates for today's sessions;
// the other statuses always invalidate (cancelling a future session must refresh lists)
export const shouldInvalidateForSession = (sessionRecord: any) => {
    if (sessionRecord?.status !== "confirmed") return true;
    if (!sessionRecord.start_date) return true;

    const isToday = isSameDay(new Date(sessionRecord.start_date), new Date());
    return isToday;
};

// Only read once when the calendar screen mounts, so refreshing it on every session event is wasted work.
export const CLINICS_FILTER_QUERY_KEY = `sessions-records-${TECHNICIAN_WEEK_CLINICS_QUERY_KEY_VALUE}`;

export const shouldInvalidateSessionsQuery = (queryKey: string | undefined) => {
    if (!queryKey) return false;
    if (queryKey === CLINICS_FILTER_QUERY_KEY) return false;

    const isSessionsListQuery = queryKey.includes("sessions-records");
    return isSessionsListQuery;
};

export const useSessionsRecordsInvalidate = () => {
    const queryClient = useQueryClient();
    const debounceTimeout = useRef<ReturnType<typeof setTimeout>>();

    const handleSessionsRecordsEvent = useCallback(
        (subscriptionData: SubscriptionData) => {
            const isDataEvent = ["create", "update", "delete"].includes(subscriptionData.event);
            if (!isDataEvent) return;

            const sessionRecords = Array.isArray(subscriptionData.data) ? subscriptionData.data : [];
            const hasRelevantSession = sessionRecords.length === 0 || sessionRecords.some(shouldInvalidateForSession);
            if (!hasRelevantSession) return;

            clearTimeout(debounceTimeout.current);
            debounceTimeout.current = setTimeout(() => {
                queryClient.invalidateQueries({
                    predicate: (query) => shouldInvalidateSessionsQuery(query.queryKey[0] as string),
                });
            }, INVALIDATE_DEBOUNCE_MS);
        },
        [queryClient]
    );

    return { handleSessionsRecordsEvent };
};
